import { useState } from "react";
import { useTranslation } from "react-i18next";

import { OptionGrid } from "../components/game/OptionGrid.js";
import { Timeline } from "../components/game/Timeline.js";
import { Vinyl } from "../components/game/Vinyl.js";
import type { TimelineCardView } from "../services/generated/client.js";
import { cn } from "../lib/utils.js";

/**
 * How to play — phase 24, a scripted demo over canned state.
 *
 * # 🔴 Why a demo and not coach-marks over a live game
 *
 * The maintainer's call, and the roadmap named the reason before it was built:
 * *"the walkthrough has to survive every layout change made to the screens it
 * points at — that is the real cost and it is ongoing"*. An overlay anchored to
 * real controls breaks **silently** every time somebody moves one, and the
 * breakage shows up in front of the one person who cannot tell a bug from the
 * game.
 *
 * This renders the same `Timeline`, `OptionGrid` and `Vinyl` the game does, with
 * canned state. It survives a layout change by construction, because it *is* the
 * layout — the same argument that makes `just preview` worth having, and it
 * reuses the components that harness already proved render in isolation.
 *
 * What it gives up is teaching the actual buttons in situ. That is the trade,
 * and it is cheap here because ADR 0026 already put every control **in the stage
 * at the moment it applies** — there is no drawer left to find.
 *
 * # It teaches the shape of a turn, not the rules
 *
 * Five steps and no scoring table. Phase 23 is where the rules live, and this
 * ends by pointing at it — a walkthrough that tried to carry the challenge
 * scoring, each mode's settlement and every price would be a worse version of
 * that page that somebody has to tap through.
 *
 * # Interruptible, always
 *
 * *Skip* is on every step and is the same size as *Next*. A tutorial you cannot
 * leave is the thing people mean when they say they hate tutorials.
 */

/** Four songs, deliberately far apart, so the ordering is obvious at a glance. */
const CARDS: TimelineCardView[] = [
  { artist: "Nina Simone", title: "Sinnerman", track: "demo-1", year: 1965 },
  { artist: "Queen", title: "Bohemian Rhapsody", track: "demo-2", year: 1975 },
  { artist: "Suzanne Vega", title: "Luka", track: "demo-3", year: 1987 },
];

/**
 * The steps, with their keys spelled out.
 *
 * ⚠️ **Not `howToPlay.${step}.title`.** The i18n keys are statically typed
 * (`locales/shape.ts`), and a template literal throws that away — a renamed or
 * deleted key becomes a blank line on somebody's phone instead of a compile
 * error. TypeScript refused the templated version, which is the typing doing
 * exactly the job `frontend.md` keeps it for.
 */
const STEPS = [
  { title: "howToPlay.0.title", body: "howToPlay.0.body" },
  { title: "howToPlay.1.title", body: "howToPlay.1.body" },
  { title: "howToPlay.2.title", body: "howToPlay.2.body" },
  { title: "howToPlay.3.title", body: "howToPlay.3.body" },
  { title: "howToPlay.4.title", body: "howToPlay.4.body" },
] as const;

export function HowToPlay({
  onDone,
  onOpenRules,
}: {
  /** Finished or skipped — the caller decides what that means for a profile. */
  onDone: () => void;
  /** Open the full rules. Omitted where there is nowhere to send them. */
  onOpenRules?: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  // Local, and never sent anywhere: the demo's own answers exist so the screen
  // reacts to a tap, which is the only thing that makes it a walkthrough rather
  // than a slideshow.
  const [artist, setArtist] = useState<number | null>(null);
  const [slot, setSlot] = useState<number | null>(null);

  const last = step === STEPS.length - 1;
  const copy = STEPS[step] ?? STEPS[0];

  return (
    <div
      data-testid="how-to-play"
      className="flex min-h-full flex-col gap-5 bg-stage-950 p-5"
    >
      <header>
        <div className="flex items-start gap-3">
          <p className="text-xs uppercase tracking-[0.2em] text-flash-500">
            {t("howToPlay.step", { n: step + 1, of: STEPS.length })}
          </p>
          {/* ⚠️ **Skip moved up here** when Back was added, and it had to: the
              footer now holds *step navigation*, and a row of Skip / Back / Next
              makes leaving and going back look like the same kind of act. It is
              still on every step, which is the property that matters. */}
          <button
            type="button"
            onClick={onDone}
            className="ml-auto -mt-1 min-h-11 shrink-0 px-2 text-sm font-medium text-white/50"
          >
            {t("howToPlay.skip")}
          </button>
        </div>
        <h1 className="mt-1 text-2xl font-semibold">{t(copy.title)}</h1>
        <p className="mt-2 text-sm text-white/70">{t(copy.body)}</p>
      </header>

      <div className="min-h-64 grow">
        <Stage
          step={step}
          artist={artist}
          slot={slot}
          onArtist={setArtist}
          onSlot={setSlot}
        />
      </div>

      {/* Where the rules live. On the last step only — offering it earlier would
          send somebody away mid-lesson to read the thing this exists to make
          unnecessary on a first evening. */}
      {last && onOpenRules && (
        <button
          type="button"
          onClick={onOpenRules}
          className="min-h-12 rounded-lg border border-white/20 px-4 text-sm font-medium text-white/70"
        >
          {t("howToPlay.fullRules")}
        </button>
      )}

      {/* 🔴 **Back exists because the maintainer hit its absence on a real
          phone.** Two of these steps ask you to tap something, and a stepper
          that only goes forward means a misread step is gone for good — you
          either restart the whole thing from the menu or you do not get it.

          Not rendered at all on the first step rather than rendered disabled: a
          dead control is a worse answer than no control, and `Next` keeps its
          place either way because it is pushed right by `ml-auto`. */}
      <nav className="flex items-center gap-3">
        {step > 0 && (
          <button
            type="button"
            onClick={() => {
              setStep((n) => n - 1);
            }}
            className="min-h-12 rounded-lg border border-white/20 px-4 text-sm font-medium text-white/70"
          >
            {t("howToPlay.back")}
          </button>
        )}
        <Dots step={step} />
        <button
          type="button"
          onClick={
            last
              ? onDone
              : () => {
                  setStep((n) => n + 1);
                }
          }
          className="ml-auto min-h-12 rounded-lg bg-flash-500 px-6 font-semibold text-stage-950"
        >
          {last ? t("howToPlay.done") : t("howToPlay.next")}
        </button>
      </nav>
    </div>
  );
}

/** What the step is showing. The real components, with made-up state. */
function Stage({
  step,
  artist,
  slot,
  onArtist,
  onSlot,
}: {
  step: number;
  artist: number | null;
  slot: number | null;
  onArtist: (n: number) => void;
  onSlot: (n: number) => void;
}) {
  const { t } = useTranslation();

  switch (step) {
    case 0:
      // The clip. Spinning, because that is what the screen actually does while
      // one plays, and a still record reads as a stopped game.
      return (
        <div className="flex flex-col items-center gap-4 py-6">
          <Vinyl spinning size={160} />
          <p className="text-sm text-white/50">{t("howToPlay.0.caption")}</p>
        </div>
      );

    case 1:
      // The grids, and they answer to a tap: a walkthrough where nothing reacts
      // is a slideshow with extra steps.
      return (
        <OptionGrid
          label={t("tally.artist")}
          options={[
            "Nina Simone",
            "Queen",
            "Suzanne Vega",
            "Kate Bush",
            "Blondie",
            "Talk Talk",
          ]}
          selected={artist}
          onSelect={onArtist}
        />
      );

    case 2:
      // The timeline with a gap to choose. Two cards rather than three, so the
      // three gaps are visibly "before, between, after" rather than a row.
      return (
        <Timeline
          cards={CARDS.slice(0, 2)}
          selectedSlot={slot}
          onPlace={onSlot}
          cardsToWin={10}
        />
      );

    case 3:
      // The answer, and a full timeline — what a correct placement produces.
      return (
        <div className="flex flex-col gap-4">
          <p className="text-center text-3xl font-semibold tabular-nums text-flash-400">
            1987
          </p>
          <Timeline cards={CARDS} cardsToWin={10} />
        </div>
      );

    default:
      return (
        <div className="flex flex-col items-center justify-center gap-4 py-8 text-center">
          <Vinyl spinning={false} size={120} />
          <p className="max-w-sm text-sm text-white/60">{t("howToPlay.4.caption")}</p>
        </div>
      );
  }
}

/** Where you are. Decorative — the header already says it in words. */
function Dots({ step }: { step: number }) {
  return (
    <span aria-hidden="true" className="flex gap-1.5">
      {STEPS.map((each, n) => (
        <span
          // Keyed on the step's own copy key rather than the index: they are
          // unique, they are literals, and the list never reorders.
          key={each.title}
          className={cn(
            "size-2 rounded-full",
            n === step ? "bg-flash-500" : "bg-white/20",
          )}
        />
      ))}
    </span>
  );
}
